import {HStack, Icon, IconButton, IconButtonProps, Spinner, Text} from "@chakra-ui/react";
import {Dispatch, memo, ReactNode, SetStateAction, Suspense, useCallback, useMemo, useRef, useState} from "react";
import {XMarkIcon} from "@heroicons/react/16/solid";
import {AnyCondition, Filter, isFilterValueEmpty} from "../../../../hooks/filter/filterState";
import {AnyFilterDefinition} from "../../../../hooks/filter/filterConfig";
import {MenuContent, MenuRadioItem, MenuRadioItemGroup, MenuRoot, MenuTrigger} from "../../../ui/menu";
import {Button} from "../../../ui/button";
import {typedMemo} from "../../../../utils/typescript";
import {Tooltip} from "../../../ui/tooltip";
import {FacetCount} from "platformed-client/types";
import T from "platformed-components/i18n/T";

const Label = memo(({icon, text}: {icon: ReactNode; text: ReactNode}) => {
  return (
    <HStack px={2} fontWeight="medium" borderRightWidth="1px" whiteSpace="nowrap" flex="0 0 auto">
      {icon != null && (
        <Icon boxSize="1em" color="fg.muted">
          {icon}
        </Icon>
      )}
      {text}
    </HStack>
  );
});

const RemoveButton = memo((props: IconButtonProps) => {
  return (
    <IconButton size="2xs" variant="ghost" rounded="none" {...props}>
      <XMarkIcon />
    </IconButton>
  );
});

function getConditionLabel(condition: AnyCondition, filterValue: unknown) {
  const label = condition.label;
  return label instanceof Function ? label(filterValue) : label;
}

const ConditionMenu = typedMemo(
  <D extends AnyFilterDefinition>({
    conditions,
    filter,
    onSelectedConditionChange,
  }: {
    conditions: readonly AnyCondition[];
    filter: Filter<D>;
    onSelectedConditionChange: (selectedCondition: string) => void;
  }) => {
    const conditionLabels = useMemo(
      () => Object.fromEntries(conditions.map(c => [c.value, getConditionLabel(c, filter.value)])),
      [conditions, filter.value],
    );
    const [open, setOpen] = useState(false);

    return (
      <MenuRoot
        open={open}
        onOpenChange={e => {
          setOpen(e.open);
        }}
        unmountOnExit
        lazyMount
      >
        <MenuTrigger asChild>
          <Button
            size="2xs"
            variant="ghost"
            rounded="none"
            color="fg.muted"
            fontWeight="normal"
            borderWidth="0"
            borderRightWidth="1px"
            borderColor="border"
            flex="0 0 auto"
          >
            {conditionLabels[filter.condition]}
          </Button>
        </MenuTrigger>
        <MenuContent>
          <MenuRadioItemGroup value={filter.condition} onValueChange={e => onSelectedConditionChange(e.value)}>
            {conditions.map(condition => (
              <MenuRadioItem key={condition.value} value={condition.value}>
                {conditionLabels[condition.value]}
              </MenuRadioItem>
            ))}
          </MenuRadioItemGroup>
        </MenuContent>
      </MenuRoot>
    );
  },
);

export const FilterChip = typedMemo(
  <D extends AnyFilterDefinition>({
    definition,
    filter,
    onApply,
    onRemove,
    onOpenChange,
    disableRemove = false,
    facet,
    context,
  }: {
    definition: D;
    filter: Filter<D>;
    onApply: Dispatch<SetStateAction<Filter<D>>>;
    onRemove: () => void;
    onOpenChange: (open: boolean) => void;
    disableRemove?: boolean;
    facet: FacetCount[];
    context: object;
  }) => {
    const rootRef = useRef<HTMLDivElement>(null);
    const menuTriggerRef = useRef<HTMLButtonElement>(null);

    type OpenState = {initialFilter: Filter<D>};

    const [openState, setOpenState] = useState<OpenState | null>(null);
    const [menuAnchorLeftOffset, setMenuAnchorLeftOffset] = useState(0);

    const innerOnOpenChange = useCallback(
      (open: boolean) => {
        setOpenState(open ? {initialFilter: filter} : null);
        onOpenChange(open);

        if (open) {
          // Store the current position of the trigger element when the menu opens.
          // This ensures the menu remains anchored in a stable position, preventing
          // it from jumping around if the trigger element is repositioned due to
          // changes in conditions when the user edits the filters.
          setMenuAnchorLeftOffset(menuTriggerRef.current!.offsetLeft);
        }
      },
      [filter, onOpenChange],
    );

    const isEmpty = useMemo(() => isFilterValueEmpty(definition, filter), [definition, filter]);

    return (
      <HStack
        ref={rootRef}
        position="relative"
        fontSize="xs"
        gap={0}
        bg="white"
        rounded="sm"
        borderWidth="1px"
        borderColor="border"
        alignItems="stretch"
      >
        <Label icon={definition.icon} text={definition.label} />
        <ConditionMenu
          conditions={definition.conditions}
          filter={filter}
          onSelectedConditionChange={condition => {
            onApply(prev => ({...prev, condition}));
          }}
        />
        {definition.components.Picker != null ? (
          <MenuRoot
            open={openState != null}
            onOpenChange={d => innerOnOpenChange(d.open)}
            positioning={{
              hideWhenDetached: true,
              placement: "bottom-start",
              getAnchorRect: () => rootRef.current!.getBoundingClientRect(),
              shift: menuAnchorLeftOffset,
            }}
            lazyMount
            unmountOnExit
            composite={false}
            closeOnSelect={false}
            onExitComplete={() => setMenuAnchorLeftOffset(0)}
          >
            <MenuTrigger asChild ref={menuTriggerRef}>
              <Button
                size="2xs"
                variant="ghost"
                rounded="none"
                borderWidth="0"
                borderRightWidth="1px"
                borderColor="border"
                flex="0 0 auto"
              >
                {isEmpty ? (
                  <Text>...</Text>
                ) : (
                  <Suspense fallback={<Spinner boxSize={2} mx={4} color="fg.subtle" />}>
                    <definition.components.Chip filter={filter} context={context} />
                  </Suspense>
                )}
              </Button>
            </MenuTrigger>
            <MenuContent>
              <definition.components.Picker
                facet={facet}
                filter={filter}
                onClose={() => innerOnOpenChange(false)}
                onApply={onApply}
                context={context}
              />
            </MenuContent>
          </MenuRoot>
        ) : (
          <Button
            pointerEvents="none"
            size="2xs"
            variant="ghost"
            rounded="none"
            borderWidth="0"
            borderRightWidth="1px"
            borderColor="border"
            flex="0 0 auto"
          >
            {isEmpty ? (
              <Text>...</Text>
            ) : (
              <Suspense fallback={<Spinner boxSize={2} mx={4} color="fg.subtle" />}>
                <definition.components.Chip filter={filter} context={context} />
              </Suspense>
            )}
          </Button>
        )}
        <Tooltip content={<T keyName="saved_views.filter.at_least_one_required" />} disabled={!disableRemove}>
          <RemoveButton onClick={onRemove} disabled={disableRemove} />
        </Tooltip>
      </HStack>
    );
  },
);
